/**
 * When a navigation she started may become the document load that switches her to a new
 * version (T890 — `pwa-contract.md` §4).
 *
 * A client navigation keeps the old bundle and the old worker; only a document load can
 * reach the new ones. So once a new version waits, the next navigation she starts inside
 * the app is turned into an ordinary page load — except where a page load could cost her
 * something a client navigation keeps. Pure: the caller passes plain values in.
 *
 * The rule judges a NAVIGATION, not a click (infra audit round 1, defect 1): it runs from
 * React Router's blocker, which only sees a navigation the page actually started. A link
 * whose own handler cancels it — the auto-test's «Salir del test» asking whether she wants
 * to lose her answers — never reaches it, so the question is always asked.
 */

/** The session player. Also the rule `root.tsx` uses to stand the global chrome down. */
export function isPlayerPath(pathname: string): boolean {
  return pathname.includes("/reproductor") || pathname.includes("/player/");
}

export interface SwitchInput {
  /** `location.href` of the page she is on. */
  readonly from: string;
  /** Where the navigation goes, absolute or relative to `from`. */
  readonly to: string;
  readonly hasUnsavedInput: boolean;
}

/**
 * The URL to load as a document, or `null` when the navigation must stay a client one.
 */
export function switchHref({ from, to, hasUnsavedInput }: SwitchInput): string | null {
  let next: URL;
  let current: URL;
  try {
    current = new URL(from);
    next = new URL(to, current);
  } catch {
    return null;
  }
  if (next.origin !== current.origin) return null;
  // Same page with another query or hash: a filter, a tab, an anchor. She stays "there".
  if (next.pathname === current.pathname) return null;
  // Nothing reloads into or within a session; the way out of the player may switch.
  if (isPlayerPath(next.pathname)) return null;
  // A form she typed into can survive a client navigation (a form in a layout); a document
  // load would wipe it. The switch waits for a navigation with nothing to lose.
  if (hasUnsavedInput) return null;
  return next.href;
}

interface EditableField {
  readonly isConnected: boolean;
  readonly form: unknown;
}

/**
 * Remembers the fields she typed into for as long as they stay on the page.
 *
 * Event-based on purpose: React keeps a controlled input's `value` attribute in step with
 * its state, so comparing `value` with `defaultValue` reads every controlled form as clean.
 * A field outside any `<form>` (a player slider, a search box in the chrome) is not "a form
 * with unsaved input" and is never recorded.
 *
 * A submit does not clear it (AI review of #2079): the native `submit` event fires before
 * the action answers and even when client-side validation cancels the send, so a form
 * showing a validation error would read as clean while it still holds her text. Holding the
 * switch until the form leaves the page costs one navigation on a page-level form. A form
 * that outlives navigations (a layout's form) holds it until the next document load; at
 * T890 only the Back Office's layouts carry one (`admin.audit`, `admin.users.$id`) — no
 * member or public layout does (AI review round 2 of #2079).
 */
export function createEditTracker() {
  const fields = new Set<EditableField>();
  return {
    edited(field: EditableField): void {
      if (field.form) fields.add(field);
    },
    hasUnsavedInput(): boolean {
      for (const field of fields) {
        // A field the navigation unmounted is gone with its input; it no longer counts.
        if (!field.isConnected) fields.delete(field);
      }
      return fields.size > 0;
    },
  };
}
